/**
 * Number and currency formatting utilities
 */

import { BigDecimal } from "@solomon-labs/bigdecimal";

type DecimalInput = BigDecimal | number | string;

const ONE_THOUSAND = new BigDecimal("1000");
const ONE_MILLION = new BigDecimal("1000000");
const ONE_BILLION = new BigDecimal("1000000000");

function parseDecimal(value: DecimalInput): BigDecimal | null {
    try {
        const decimal = value instanceof BigDecimal ? value : new BigDecimal(value);

        return decimal.isNaN() ? null : decimal;
    } catch {
        return null;
    }
}

function trimFractionDigits(value: string, minimumFractionDigits: number): string {
    const [wholePart, fractionPart] = value.split(".");

    if (!fractionPart) {
        return value;
    }

    let trimmedFraction = fractionPart;

    while (trimmedFraction.length > minimumFractionDigits && trimmedFraction.endsWith("0")) {
        trimmedFraction = trimmedFraction.slice(0, -1);
    }

    return trimmedFraction.length > 0 ? `${wholePart}.${trimmedFraction}` : wholePart;
}

function applyPrefix(value: string, prefix: string): string {
    if (!prefix) {
        return value;
    }

    return value.startsWith("-") ? `-${prefix}${value.slice(1)}` : `${prefix}${value}`;
}

function formatDecimalValue(
    value: DecimalInput,
    {
        minimumFractionDigits = 0,
        maximumFractionDigits = 2,
        truncate = false,
        withCommas = true,
        prefix = ""
    }: FormatNumberOptions & { withCommas?: boolean; prefix?: string } = {}
): string {
    const decimal = parseDecimal(value);

    if (!decimal) {
        return applyPrefix(minimumFractionDigits > 0 ? `0.${"0".repeat(minimumFractionDigits)}` : "0", prefix);
    }

    const normalizedMaximumFractionDigits = Math.max(minimumFractionDigits, maximumFractionDigits);
    const adjustedDecimal = truncate
        ? decimal.truncate(normalizedMaximumFractionDigits)
        : decimal.round(normalizedMaximumFractionDigits);
    const formattedValue = adjustedDecimal.toFixed(normalizedMaximumFractionDigits, withCommas);

    return applyPrefix(trimFractionDigits(formattedValue, minimumFractionDigits), prefix);
}

/**
 * Options for formatting numbers
 */
export interface FormatNumberOptions {
    /** Minimum decimal places (default 0) */
    minimumFractionDigits?: number;
    /** Maximum decimal places (default 2) */
    maximumFractionDigits?: number;
    /** Floor-truncate to maximumFractionDigits instead of rounding. Use for balance displays to never overstate. */
    truncate?: boolean;
}

/**
 * Format a number with commas and decimal places
 *
 * @param value - Number or string to format
 * @param options - Formatting options
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234567.89) // "1,234,567.89"
 * formatNumber("1000", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) // "1,000.00"
 * formatNumber(1234.5678, { maximumFractionDigits: 4 }) // "1,234.5678"
 */
export function formatNumber(value: DecimalInput, options: FormatNumberOptions = {}): string {
    return formatDecimalValue(value, options);
}

/**
 * Format a value as USD currency
 *
 * @param value - Number or string to format
 * @param minDecimals - Minimum decimal places (default 2)
 * @param maxDecimals - Maximum decimal places (default 2)
 * @returns Formatted currency string with $ prefix
 *
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency("1000") // "$1,000.00"
 */
export function formatCurrency(value: DecimalInput, minDecimals: number = 2, maxDecimals: number = 2): string {
    return formatDecimalValue(value, {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
        prefix: "$"
    });
}

/**
 * Format a large currency value in compact notation (k, M, B)
 *
 * @param value - Number or string to format
 * @param decimals - Decimal places to show (default 1)
 * @returns Formatted compact currency string
 *
 * @example
 * formatCurrencyCompact(1500000) // "$1.5M"
 * formatCurrencyCompact("250000", 2) // "$250.00k"
 */
export function formatCurrencyCompact(value: DecimalInput, decimals: number = 1): string {
    const decimal = parseDecimal(value);

    if (!decimal) {
        return "$0";
    }

    const absoluteValue = decimal.abs();

    if (absoluteValue.gte(ONE_BILLION)) {
        return `${applyPrefix(decimal.divide(ONE_BILLION).toFixed(decimals), "$")}B`;
    }

    if (absoluteValue.gte(ONE_MILLION)) {
        return `${applyPrefix(decimal.divide(ONE_MILLION).toFixed(decimals), "$")}M`;
    }

    if (absoluteValue.gte(ONE_THOUSAND)) {
        return `${applyPrefix(decimal.divide(ONE_THOUSAND).toFixed(decimals), "$")}k`;
    }

    return formatCurrency(decimal, 0, decimals);
}

/**
 * Format a percentage value
 *
 * @param value - Percentage value (15.2 means 15.2%)
 * @param decimals - Decimal places to show (default 1)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercent(15.23) // "15.2%"
 * formatPercent(5, 2) // "5.00%"
 */
export function formatPercent(value: DecimalInput, decimals: number = 1): string {
    const decimal = parseDecimal(value);

    if (!decimal) {
        return "0%";
    }

    return `${decimal.round(decimals).toFixed(decimals)}%`;
}

/**
 * Truncate an address for display
 *
 * @param address - Full address string
 * @param startChars - Number of characters to show at start (default 4)
 * @param endChars - Number of characters to show at end (default 4)
 * @returns Truncated address
 *
 * @example
 * truncateAddress("7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU")
 * // "7xKX...AsU"
 */
export function truncateAddress(address: string, startChars: number = 4, endChars: number = 4): string {
    if (address.length <= startChars + endChars) {
        return address;
    }
    return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
}

/**
 * Formats a raw token amount (BigDecimal) to user viewable format (takes decimals into account)
 *
 * @param num - BigDecimal amount in raw token units
 * @param decimals - Token decimals
 * @param withCommas - Add thousand separators (default true)
 * @param minDecimals - Minimum decimal places (default 2)
 * @param maxDecimals - Maximum decimal places (default 4)
 * @returns Formatted token amount string
 *
 * @example
 * formatTokenNumber(new BigDecimal("1234567890"), 6, true, 2, 4) // "1,234.5679"
 */
export function formatTokenNumber(
    num: BigDecimal,
    decimals: number,
    withCommas: boolean = true,
    minDecimals: number = 2,
    maxDecimals: number = 4
): string {
    // Convert the BigDecimal to a string (get the raw bigint value)
    const bnString = num.toBigint().toString();

    // Pad the string with leading zeros if it's too short
    const paddedString = bnString.padStart(decimals + 1, "0");

    // Insert the decimal point at the correct position
    let integerPart = paddedString.slice(0, -decimals);
    let decimalPart = paddedString.slice(-decimals);

    if (decimalPart.length > maxDecimals) {
        decimalPart = decimalPart.slice(0, maxDecimals);
    } else if (decimalPart.length < minDecimals) {
        decimalPart = decimalPart.padEnd(minDecimals, "0");
    }

    if (withCommas) {
        // Add commas to the integer part
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        // Combine the formatted integer part with the decimal part
        return `${integerPart}.${decimalPart}`;
    }

    return `${integerPart}.${decimalPart}`;
}

/**
 * Safe toNumber conversion for BigDecimal values.
 * Handles BigDecimal instances, plain numbers, and unknown types gracefully.
 */
export function safeToNumber(value: unknown): number {
    if (value && typeof (value as { toNumber?: () => number }).toNumber === "function") {
        return (value as { toNumber: () => number }).toNumber();
    }
    if (typeof value === "number") return value;
    return 0;
}

/**
 * Dynamically show different significant digits based on the amount
 *
 * @param amount - Numeric amount to format
 * @returns Formatted string with appropriate significant digits
 *
 * @example
 * formatNumberSignificantDigits(1234567) // "1234567"
 * formatNumberSignificantDigits(1234.56) // "1234.56"
 * formatNumberSignificantDigits(12.345678) // "12.3457"
 * formatNumberSignificantDigits(1.23456789) // "1.234568"
 * formatNumberSignificantDigits(0.123456) // "0.123456"
 */
export function formatNumberSignificantDigits(amount: number): string {
    if (amount === 0) return "0";

    let formatted: string;

    if (amount >= 1000000) {
        formatted = amount.toFixed(0);
    } else if (amount >= 1000) {
        formatted = amount.toFixed(2);
    } else if (amount >= 10) {
        formatted = amount.toFixed(4);
    } else if (amount >= 1) {
        formatted = amount.toFixed(6);
    } else {
        // < 1: show all decimals without rounding
        formatted = amount.toString();
    }

    return formatted.replace(/\.?0+$/, "");
}
